import { ExportOutlined } from '@ant-design/icons';
import { DatePicker, Table, Tag, Typography } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import SalesRepFilter from '../../components/ui/SalesRepFilter';
import SearchInput from '../../components/ui/SearchInput';
import { usePersistentFilters } from '../../hooks/usePersistentFilters';
import { getCreditReport } from '../../redux/actions/reports';
import { getRoutes, ROLES } from '../../routes/Routes';
import { formatCurrency, getBalanceColor } from '../../utils';

const defaultFilters = {
    delivery_date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
    salesrep_netsuite_id: null,
};

const CreditReport = () => {
    const { user } = useSelector((state) => state.authSlice);

    const { filters, applyFilters } = usePersistentFilters(defaultFilters)
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchCreditReport = async (body) => {
        try {
            setLoading(true);
            const res = await getCreditReport(body);
            if (res?.ack) {
                setReports(res.customers || []);
            }
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Customer',
            dataIndex: 'customer_name',
            key: 'customer_name',
            width: 300,
            render: (text, record) => (
                <div className="inline-flex align-items-center gap-1 max-w-25rem">
                    <Typography.Text
                        ellipsis={{ tooltip: true, }}
                        className="m-0 max-w-full"
                    >
                        {record?.customer_code} - {text}
                    </Typography.Text>
                    {user?.role !== ROLES.CUSTOMER && (
                        <Link
                            to={getRoutes().customer_details.route(
                                record?.customer_netsuite_id
                            )}
                        >
                            <ExportOutlined />
                        </Link>
                    )}
                </div>
            ),
        },
        {
            title: 'Sales Rep',
            dataIndex: 'salesrep_name',
            key: 'salesrep_name',
        },
        {
            title: 'Terms',
            dataIndex: 'terms',
            key: 'terms',
        },
        {
            title: 'Credit Limit',
            dataIndex: 'credit_limit',
            key: 'credit_limit',
            render: (text) => Number(text) ? <Typography.Text strong>{formatCurrency(Number(text) || 0)}</Typography.Text> : null,
        },
        {
            title: 'Balance',
            dataIndex: 'balance',
            key: 'balance',
            render: (text, record) => {
                if (!Number(text)) return;
                const color = getBalanceColor({
                    balance: record?.balance,
                    creditLimit: record?.credit_limit,
                });
                return <Tag className="font-bold" color={color + '-inverse'}>{formatCurrency(Number(text) || 0)}</Tag>
            }
        },
        {
            title: 'Balance %',
            key: 'balance_percentage',
            render: (_, record) => {
                const creditLimit = Number(record?.credit_limit) || 0;
                const balance = Number(record?.balance) || 0;
                const color = getBalanceColor({
                    balance: record?.balance,
                    creditLimit: record?.credit_limit,
                });
                if (creditLimit <= 0) return null;
                const percentage = (balance / creditLimit) * 100;
                return <Typography.Text strong style={{ color }}>
                    {percentage.toFixed(2)}%
                </Typography.Text>;
            },
        },
        {
            title: 'Overdue Balance',
            dataIndex: 'overdue_balance',
            key: 'overdue_balance',
            render: (text, record) => {
                if (!Number(text)) return;
                return <Tag className="font-bold" color={Number(text) > 0 ? 'red-inverse' : 'gray'}>{formatCurrency(Number(text) || 0)}</Tag>
            }
        },
        {
            title: 'Unbilled Orders',
            dataIndex: 'unbilled_orders',
            key: 'unbilled_orders',
            render: (text) => Number(text) ? <Typography.Text strong>{formatCurrency(Number(text) || 0)}</Typography.Text> : null,
        },
    ];

    useEffect(() => {
        const deliveryDate = dayjs(filters?.delivery_date).format('YYYY-MM-DD')

        fetchCreditReport(
            applyFilters({ delivery_date: deliveryDate })
        );
    }, []);

    const filteredReports = reports.filter((report) => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            report?.customer_name?.toLowerCase().includes(query) ||
            report?.customer_code?.toLowerCase().includes(query)
        );
    });

    return (
        <div className="promotions-page">
            <div className="page-header mb-3">
                <div className="flex justify-content-between align-items-center mb-2">
                    <Typography.Title level={3} className="page-title">
                        Credit Report
                    </Typography.Title>
                </div>
                <div className="flex align-items-center gap-2 flex-wrap">
                    <SearchInput
                        placeholder="Search Customer"
                        size="large"
                        className="lg:max-w-15rem w-full"
                        value={searchQuery}
                        onSearch={(val) => setSearchQuery(val)}
                    />
                    <DatePicker
                        placeholder="Delivery Date"
                        format="DD-MM-YYYY"
                        size="large"
                        className="lg:max-w-15rem w-full"
                        value={filters.delivery_date ? dayjs(filters.delivery_date) : null}
                        onChange={(date) => {
                            const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : null;
                            const nextFilters = applyFilters({ delivery_date: formattedDate });
                            fetchCreditReport({ ...nextFilters, page: 1 });
                        }}
                        showNow={false}
                        allowClear={false}
                    />
                    <SalesRepFilter
                        isNetsuiteId
                        value={filters.salesrep_netsuite_id}
                        onChange={(value) => {
                            const nextFilters = applyFilters({ salesrep_netsuite_id: value });
                            fetchCreditReport({ ...nextFilters, page: 1 });
                        }}
                    />
                </div>
            </div>

            <Table
                size="small"
                columns={columns}
                rowKey={(record) => record?.id}
                rowClassName={(record) => record?.terms?.includes('Cash on Delivery') ? 'cod-row' : ''}
                loading={loading}
                dataSource={filteredReports}
                pagination={false}
                scroll={{ x: 'max-content', y: 'calc(100vh - 330px)' }}
                components={{
                    body: {
                        cell: ({ className, ...rest }) => (
                            <td
                                {...rest}
                                className={reports?.length && clsx(className, 'py-1')}
                            />
                        ),
                    },
                }}
            />
        </div>)
}

export default CreditReport